import { HISTORY_EVENT_TYPES, type BookHistoryEvent, type BookHistoryEventType, type BookHistoryPayload } from './types';

// Lenient decoding, matching BookHistoryPayload.fromJson: ints and doubles
// accept numbers or numeric strings, strings are trimmed (blank → absent),
// a non-object root decodes to an empty payload.

function toInt(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.trunc(v) : undefined;
  if (typeof v === 'string' && /^\s*[+-]?\d+\s*$/.test(v)) return Number.parseInt(v, 10);
  return undefined;
}

function toDouble(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function toText(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = (typeof v === 'string' ? v : String(v)).trim();
  return s ? s : undefined;
}

export function trimToNull(v: unknown): string | null {
  return toText(v) ?? null;
}

export function decodePayload(raw: unknown): BookHistoryPayload {
  let src = raw;
  if (typeof src === 'string') {
    try {
      src = JSON.parse(src);
    } catch {
      return {};
    }
  }
  if (!src || typeof src !== 'object' || Array.isArray(src)) return {};
  const o = src as Record<string, unknown>;
  return encodePayload({
    fromPositionMs: toInt(o.fromPositionMs),
    toPositionMs: toInt(o.toPositionMs),
    chapterId: toText(o.chapterId),
    chapterTitle: toText(o.chapterTitle),
    chapterIndex: toInt(o.chapterIndex),
    bookmarkId: toText(o.bookmarkId),
    note: toText(o.note),
    playbackSpeed: toDouble(o.playbackSpeed),
    sleepTimerDurationMs: toInt(o.sleepTimerDurationMs),
    reason: toText(o.reason),
  });
}

/** Drops absent and blank fields (the JSON form omits nulls). */
export function encodePayload(p: BookHistoryPayload): BookHistoryPayload {
  const out: BookHistoryPayload = {};
  for (const [k, v] of Object.entries(p) as Array<[keyof BookHistoryPayload, unknown]>) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string') {
      const t = v.trim();
      if (t) (out as Record<string, unknown>)[k] = t;
    } else if (typeof v === 'number' && Number.isFinite(v)) {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

const TYPE_SET = new Set<string>(HISTORY_EVENT_TYPES);

/** Unknown wire values decode to `bookAction`. */
export function decodeEventType(raw: unknown): BookHistoryEventType {
  return typeof raw === 'string' && TYPE_SET.has(raw) ? (raw as BookHistoryEventType) : 'bookAction';
}

/** Validates a stored or imported record; null when it lacks an id, book or time. */
export function decodeEvent(raw: unknown): BookHistoryEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = toText(o.id);
  const bookId = toText(o.bookId);
  const timestampMs = toDouble(o.timestampMs);
  if (!id || !bookId || timestampMs == null) return null;
  return {
    id,
    bookId,
    timestampMs: Math.round(timestampMs),
    type: decodeEventType(o.type),
    payload: decodePayload(o.payload),
    sessionId: trimToNull(o.sessionId),
    source: trimToNull(o.source),
    dedupeKey: trimToNull(o.dedupeKey),
  };
}
